import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Fragment, useEffect, useRef, useState, type ReactElement } from 'react'
// `MeineRechnungZeile` beschreibt die Antwort von GET /api/me/rechnungen (services/billing.ts,
// `MyInvoiceRow`) und steht in src/client/api.ts, der einen Beschreibung der Server-Antworten.
import { api, type MeineRechnungZeile } from '../../api'
import { RequireSession } from '../../auth'
import { Betrag } from '../../components/Betrag'
import { Knopf } from '../../components/Knopf'
import { Tabelle } from '../../components/Tabelle'
import { TabellenZustand } from '../../components/Zustand'
import { datum, datumZeit } from '../../format'
import { fehlerText } from '../login'

const RECHNUNG_STATUS: Readonly<Record<MeineRechnungZeile['status'], string>> = {
  draft: 'Entwurf',
  submitted: 'Eingereicht',
  rejected: 'Abgelehnt',
  approved: 'Freigegeben',
  paid: 'Ausgezahlt',
  cancelled: 'Storniert',
}

/**
 * Was in der Zustandsspalte steht. Eine Stornorechnung wird mit `status='approved'` eingefügt
 * (Design-Entscheidung 17-Variante: „cancelled" wäre in jeder Liste zweideutig), ohne dass irgendwer
 * sie freigegeben hätte — „Freigegeben" wäre hier also eine Behauptung über einen Vorgang, den es nie
 * gab. Deshalb nennt die Zeile stattdessen, was das Dokument ist.
 */
function zustandText(rechnung: MeineRechnungZeile): string {
  return rechnung.kind === 'cancellation' ? 'Stornorechnung' : RECHNUNG_STATUS[rechnung.status]
}

/**
 * Der zugängliche Name des Rechnungsdokuments. Die Überschrift des PDF lautet „Stornorechnung
 * Nr. {number}" (pdf/rechnung.ts), also darf der Link, der es öffnet, nicht „Rechnung" heißen.
 */
function dokumentName(rechnung: MeineRechnungZeile): string {
  const art = rechnung.kind === 'cancellation' ? 'Stornorechnung' : 'Rechnung'
  return rechnung.number === null ? art : `${art} ${rechnung.number}`
}

/**
 * Der zugängliche Name des Belegs. Eine Belegnummer liefert `GET /api/me/rechnungen` nicht
 * (`MY_INVOICE_SQL` liest aus `payouts` nur `receipt_document_id`, `paid_at` und `amount_cents`),
 * deshalb benennt der Link den Beleg über die Rechnung, zu der er gehört — und erfindet keine
 * Nummer, die es auf dem Bildschirm nicht gibt.
 */
function belegName(rechnung: MeineRechnungZeile): string {
  return rechnung.number === null ? 'Auszahlungsbeleg' : `Auszahlungsbeleg zu Rechnung ${rechnung.number}`
}

/** Zurückziehen geht nur, solange niemand entschieden hat (withdrawInvoice, Spec §1.10 Schritt 2). */
function kannZurueckziehen(rechnung: MeineRechnungZeile): boolean {
  return rechnung.kind === 'invoice' && rechnung.status === 'submitted' && rechnung.decided_at === null
}

/**
 * Die Rückfrage steht bewusst als `<fieldset>` in der Tabellenzeile und nicht im modalen
 * `<Dialog>`: Wer über eine Zeile hinweg bestätigt, soll die Zeile weiter sehen — Nummer, Betrag und
 * Auftraggeber bleiben neben der Frage stehen, statt hinter einer Überblendung zu verschwinden.
 * Damit die Bestätigung trotzdem angesagt wird, wandert der Fokus beim Öffnen in die Gruppe. Er
 * landet auf der Gruppe selbst und nicht auf „Ja, zurückziehen": ein Rückfrage-Knopf, der schon
 * scharf ist, sobald er erscheint, macht aus zwei Schritten wieder einen.
 */
function Rueckfrage({
  rechnung,
  laeuft,
  onJa,
  onAbbrechen,
}: {
  rechnung: MeineRechnungZeile
  laeuft: boolean
  onJa: () => void
  onAbbrechen: () => void
}): ReactElement {
  const gruppe = useRef<HTMLFieldSetElement>(null)
  useEffect(() => {
    gruppe.current?.focus()
  }, [])

  return (
    <fieldset ref={gruppe} tabIndex={-1} aria-label="Einreichung zurückziehen">
      <p className="max-w-prose">
        Sie ziehen Ihre Einreichung über <Betrag cents={rechnung.gross_cents} /> an{' '}
        {rechnung.tenant_name} zurück. Die Positionen wandern zurück in Ihren Entwurf,{' '}
        {rechnung.tenant_name} sieht die Abrechnung nicht mehr, und es wird keine Nummer verbraucht.
      </p>
      <p className="mt-4 flex gap-4">
        <Knopf disabled={laeuft} onClick={onJa}>
          Ja, zurückziehen
        </Knopf>
        <Knopf variante="still" onClick={onAbbrechen}>Abbrechen</Knopf>
      </p>
    </fieldset>
  )
}

/** Die Anzahl der `<th>` in der Kopfzeile — Skelett und Zusatzzeilen richten sich danach. */
const SPALTEN = 7

export function RechnungenScreen(): ReactElement {
  const queryClient = useQueryClient()
  const [rueckfrage, setRueckfrage] = useState<string | null>(null)

  const liste = useQuery({
    queryKey: ['me', 'rechnungen'],
    queryFn: () => api.get<{ rechnungen: MeineRechnungZeile[] }>('/api/me/rechnungen'),
  })

  const zurueckziehen = useMutation({
    // Kein Körper: das Zurücknehmen der eigenen Einreichung hat nichts zu sagen. `{}` ist nur da,
    // weil der CSRF-Schutz einen JSON-Content-Type verlangt (src/worker/app.ts, jsonOnly).
    mutationFn: (id: string) => api.post(`/api/me/rechnungen/${encodeURIComponent(id)}/withdraw`, {}),
    onSuccess: async () => {
      setRueckfrage(null)
      // Kein Raten am Cache: die Liste wird neu geholt.
      await queryClient.invalidateQueries({ queryKey: ['me', 'rechnungen'] })
      await queryClient.invalidateQueries({ queryKey: ['me', 'provisionen'] })
    },
  })

  /**
   * Öffnen und Abbrechen laufen beide hier durch, damit die Fehlermeldung des Servers mit der
   * Rückfrage verschwindet, zu der sie gehört. Sonst stünde ein „Es liegt bereits ein Entwurf vor."
   * von vorhin oben auf der Seite, während unten eine andere Rechnung gefragt wird.
   */
  function rueckfrageSetzen(id: string | null): void {
    zurueckziehen.reset()
    setRueckfrage(id)
  }

  const rechnungen = liste.data?.rechnungen ?? []

  return (
    <div className="seite">
      <h1>Meine Abrechnungen</h1>

      {zurueckziehen.error !== null && (
        <p role="alert" className="mt-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">
          {fehlerText(zurueckziehen.error)}
        </p>
      )}

      {/*
        Laden, Fehler und Leere stehen im Tabellenkörper: die Kopfzeile bleibt über alle drei
        Zustände stehen, das Skelett hat exakt die Spaltenzahl der fertigen Tabelle, und beim
        Eintreffen der Daten springt nichts.
      */}
      <Tabelle
        beschriftung="Meine Abrechnungen und Belege"
        kopf={
          <tr>
            <th scope="col">Rechnungsnummer</th>
            <th scope="col">Auftraggeber</th>
            <th scope="col">Eingereicht am</th>
            <th scope="col" className="zahl">Brutto</th>
            <th scope="col">Status</th>
            <th scope="col">Dokumente</th>
            <th scope="col">Aktion</th>
          </tr>
        }
      >
        {liste.isPending && (
          <TabellenZustand art="laedt" spalten={SPALTEN} beschriftung="Ihre Abrechnungen werden geladen." />
        )}
        {liste.isError && (
          <TabellenZustand
            art="fehler"
            spalten={SPALTEN}
            fehler={liste.error}
            onWiederholen={() => void liste.refetch()}
          />
        )}
        {!liste.isPending && !liste.isError && rechnungen.length === 0 && (
          <TabellenZustand
            art="leer"
            spalten={SPALTEN}
            titel="Noch keine Abrechnungen"
            hinweis="Sobald Sie unter „Jetzt abrechnen“ eine Abrechnung einreichen, steht sie hier mit ihrem Stand."
            aktion={
              <a className="text-[var(--accent)] underline" href="/me/provisionen">
                Zu meinen Provisionen
              </a>
            }
          />
        )}
        {rechnungen.map((rechnung) => (
          // Eine Abrechnung kann bis zu vier <tr> belegen: die Zeile selbst, der Ablehnungsgrund,
          // die Auszahlung und die Rückfrage. Fragment mit key, weil ein <div> zwischen <tbody>
          // und <tr> kein gültiges Tabellenmarkup wäre.
          <Fragment key={rechnung.id}>
            <tr>
              <td className="zahl">{rechnung.number ?? '—'}</td>
              <td>{rechnung.tenant_name}</td>
              <td>{rechnung.submitted_at === null ? '—' : datumZeit(rechnung.submitted_at)}</td>
              <td className="zahl"><Betrag cents={rechnung.gross_cents} /></td>
              <td>{zustandText(rechnung)}</td>
              <td>
                {rechnung.document_id !== null && (
                  <a
                    className="text-[var(--accent)] underline"
                    href={`/api/dokumente/${rechnung.document_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {dokumentName(rechnung)}
                  </a>
                )}
                {rechnung.beleg_document_id !== null && (
                  <a
                    className="ml-3 text-[var(--accent)] underline"
                    href={`/api/dokumente/${rechnung.beleg_document_id}`}
                    aria-label={belegName(rechnung)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Auszahlungsbeleg
                  </a>
                )}
                {rechnung.document_id === null && rechnung.beleg_document_id === null && '—'}
              </td>
              <td>
                {kannZurueckziehen(rechnung) && (
                  <Knopf variante="still" onClick={() => rueckfrageSetzen(rechnung.id)}>
                    Einreichung zurückziehen
                  </Knopf>
                )}
                {rechnung.status === 'draft' && (
                  <a
                    className="text-[var(--accent)] underline"
                    href={`/me/abrechnen?tc=${rechnung.tenant_contractor_id}`}
                  >
                    Entwurf öffnen
                  </a>
                )}
              </td>
            </tr>
            {rechnung.status === 'rejected' && rechnung.decision_comment !== null && (
              <tr>
                <td colSpan={SPALTEN} className="text-[var(--alert)]">
                  Abgelehnt
                  {rechnung.decided_at === null ? '' : ` am ${datumZeit(rechnung.decided_at)}`}:{' '}
                  {rechnung.decision_comment}
                </td>
              </tr>
            )}
            {rechnung.payout_paid_at !== null && (
              <tr>
                <td colSpan={SPALTEN} className="leise">
                  Ausgezahlt am {datum(rechnung.payout_paid_at)}
                  {rechnung.payout_amount_cents === null ? null : (
                    <>
                      , überwiesen <Betrag cents={rechnung.payout_amount_cents} />
                    </>
                  )}
                  .
                </td>
              </tr>
            )}
            {rueckfrage === rechnung.id && (
              <tr>
                <td colSpan={SPALTEN}>
                  <Rueckfrage
                    rechnung={rechnung}
                    laeuft={zurueckziehen.isPending}
                    onJa={() => zurueckziehen.mutate(rechnung.id)}
                    onAbbrechen={() => rueckfrageSetzen(null)}
                  />
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </Tabelle>
    </div>
  )
}

/** Wie bei den übrigen /me-Bildschirmen steht der Zugangsschutz an der Route, nicht im Screen. */
function RechnungenRoute(): ReactElement {
  return (
    <RequireSession>
      <RechnungenScreen />
    </RequireSession>
  )
}

export const Route = createFileRoute('/me/rechnungen')({ component: RechnungenRoute })
